import { Injectable, Injector, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { DemoDataService } from 'src/shared/services/demo-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { environment } from 'src/environments/environment';

/**
 * The single HTTP hook for the shared sales-demo account (demos@tidy.com) and for
 * offline preview mode (product-owner click-through, no backend/login). Registered
 * before all other interceptors so it short-circuits the auth/timeout/error chain, and
 * because HttpClientCustom also routes through Angular's HttpClient, this one hook covers
 * every transport in the app. Demo sessions get two behaviors:
 *
 * 1. GET — serve the hard-coded demo response when the endpoint has a template in
 *    demo-data.json (everything else passes through untouched).
 *
 * 2. POST / PUT — the demo account is shared by many prospects at once, so NO write may
 *    touch it. Every POST/PUT is blocked EXCEPT a small allowlist needed for the visitor
 *    to leave the demo (sign in, create an account) plus the metadata endpoint the demo
 *    uses for its own tracking + theme. A blocked write never reaches the network: the
 *    same "create an account to get started" prompt every other gated demo action shows is
 *    surfaced, and the request resolves with an empty success so the caller's error path
 *    never fires. This is the robust backstop for the per-handler DemoBlockService guards.
 *
 * Preview mode is stricter on both fronts: since there's no backend to fall through to
 * (the "backend" is whatever localhost/staging happens to be configured, which a preview
 * session must never touch), untemplated GETs get a synthesized empty body instead of
 * passing through, and every non-GET is swallowed with a silent 200 — no block prompt,
 * since preview is an offline click-through with no shared account to protect.
 */
@Injectable()
export class DemoInterceptor implements HttpInterceptor {

  private demoDataService = inject(DemoDataService);
  // DemoBlockService transitively depends on HttpClient, so it must be resolved lazily
  // (at request time, after the client exists) to avoid a circular DI at construction.
  private injector = inject(Injector);

  // The only writes the demo session is allowed to make. Matched as exact, normalized API
  // paths (origin + query stripped, lowercased) so a broader endpoint can't sneak through:
  //  - api/v1/token ............... login / signup token / Google + Apple social auth
  //  - api/v1/customers ........... create account
  //  - .../recover-password ....... forgot-password (part of signing in)
  //  - api/v1/customer/recover-email  recover username (part of signing in)
  //  - api/v1/customer/metadata ... demo view tracking (recordDemoAccountView) + theme variant
  //  - api/v1/customer/utm-trackings  UTM telemetry (fires automatically on a marketing entry)
  //
  // change-password is deliberately NOT here — it is gated separately (see isBlockedWrite):
  // Auth.changePassword authenticates with the current session token, so an unconditional
  // allow would let a demo visitor rewrite the SHARED demo account's password and lock every
  // future demo out (the takeover that broke demo mode). Only a token-based recovery reset,
  // which targets the reset token's own account rather than the session, is let through.
  private static readonly ALLOWED_WRITE_PATHS = new Set([
    'api/v1/token',
    'api/v1/customers',
    'api/v1/customers/recover-password',
    'api/v1/customer/recover-email',
    'api/v1/customer/metadata',
    'api/v1/customer/utm-trackings',
  ]);

  // The session-authenticated password-change endpoint. Only a recovery-link reset (body carries
  // a reset_password_token, which scopes the change to that token's account) may pass through a
  // demo session; a plain session change would mutate the shared demo account.
  private static readonly CHANGE_PASSWORD_PATH = 'api/v1/customers/change-password';

  // Every mutating verb, not just POST/PUT: a plan cancel is a POST but "cancel all jobs"
  // (api/v1/customer/plans/cancel-all) and "cancel account" are DELETEs, which used to
  // reach the shared demo account for real.
  private static readonly BLOCKED_WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

  // A handful of data READS use POST/PUT (a request body carries the filter/query), and
  // some fire automatically on page load — e.g. the finance dashboard's
  // `api/v1/customer/dashboard/widgets/fetch`. Letting a read through can never mutate the
  // shared account, so any write whose last path segment is one of these read verbs passes
  // through instead of being blocked (which would otherwise pop the prompt on page load).
  private static readonly READ_STYLE_SUFFIXES = ['/fetch', '/search', '/query'];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.demoDataService.shouldServeTemplates()) {
      return next.handle(request);
    }
    const isPreview = this.demoDataService.isPreviewMode();

    if (request.method === 'GET') {
      const body = this.demoDataService.getDemoData(request.urlWithParams);
      if (body === null) {
        if (!isPreview) {
          return next.handle(request);
        }
        // Preview has no backend to fall through to — synthesize an empty body.
        const path = request.urlWithParams.split('?')[0];
        const lastSegment = path.split('/').filter(Boolean).pop() || '';
        const looksLikeCollection = !/^\d+$/.test(lastSegment);
        console.debug(`[preview] no template: ${path}`);
        return of(new HttpResponse({ status: 200, body: looksLikeCollection ? [] : {} }));
      }
      return of(new HttpResponse({
        status: 200,
        body,
        headers: new HttpHeaders({
          'X-Total-Records': String(Array.isArray(body) ? body.length : 1),
          'X-Total-Pages': '1',
        }),
      }));
    }

    // Non-GET: a preview session must never let a write reach the backend.
    // Swallow silently — no block prompt, since preview is an offline
    // click-through with no shared account to protect.
    if (isPreview) {
      return of(new HttpResponse({ status: 200, body: {} }));
    }

    if (this.isBlockedWrite(request)) {
      // Prompt the visitor to create/sign into a real account, then swallow the write with
      // an empty 200 so nothing mutates the shared demo account and no error toast fires.
      this.injector.get(DemoBlockService).block();
      return of(new HttpResponse({ status: 200, body: {} }));
    }

    return next.handle(request);
  }

  private isBlockedWrite(request: HttpRequest<any>): boolean {
    if (!DemoInterceptor.BLOCKED_WRITE_METHODS.has(request.method)) {
      return false;
    }
    // Only gate writes to OUR OWN backend (covers api/v1 + api/v2). External POSTs —
    // session/analytics tracking (execute-api), Sentry, PostHog, S3 uploads, etc. — never
    // touch the demo account and must pass through, or the prompt would fire on every page
    // (the session tracker POSTs on each navigation).
    if (!this.isOurBackend(request.url)) {
      return false;
    }
    const path = this.normalizePath(request.url);
    if (DemoInterceptor.ALLOWED_WRITE_PATHS.has(path)) {
      return false;
    }
    if (path === DemoInterceptor.CHANGE_PASSWORD_PATH) {
      // Allow only the recovery-link reset (token-scoped to its own account); block a
      // session-authenticated change, which would rewrite the shared demo account's password.
      return !this.isTokenBasedPasswordReset(request);
    }
    if (DemoInterceptor.READ_STYLE_SUFFIXES.some((suffix) => path.endsWith(suffix))) {
      return false;
    }
    return true;
  }

  // True when a change-password write carries a reset_password_token — the forgot-password
  // recovery flow, which the backend scopes to the token's account, so it can never touch the
  // demo session's account. Auth.changePassword sends the body as a JSON string; handle both a
  // stringified and an already-parsed object body.
  private isTokenBasedPasswordReset(request: HttpRequest<any>): boolean {
    const body = request.body;
    if (!body) {
      return false;
    }
    if (typeof body === 'string') {
      try {
        return !!JSON.parse(body)?.reset_password_token;
      } catch {
        return false;
      }
    }
    return typeof body === 'object' && !!(body as any).reset_password_token;
  }

  private isOurBackend(url: string): boolean {
    const { api_url, api_secure } = environment;
    return (!!api_url && url.startsWith(api_url)) || (!!api_secure && url.startsWith(api_secure));
  }

  // Reduce a request URL to its bare API path for allowlist comparison: drop any query /
  // fragment, strip the origin (works for both api_url and api_secure hosts), and trim
  // surrounding slashes — e.g. 'https://api.tidy.com/api/v1/token?x=1' -> 'api/v1/token'.
  private normalizePath(url: string): string {
    return url
      .split('?')[0]
      .split('#')[0]
      .replace(/^https?:\/\/[^/]+/i, '')
      .replace(/^\/+/, '')
      .replace(/\/+$/, '')
      .toLowerCase();
  }
}
